import { useVoiceOptions } from '@startupquickstart/frontend';
import { useEffect, useState } from 'react';
import { AiGenerateDialog } from '@/components/ai-job/AiGenerateDialog';

interface GenerateVoiceoversDialogProps {
  attractionId: string;
  themeId: string;
  open: boolean;
  onClose: () => void;
  /** Called after the dialog closes (whether the job finished or was handed off). */
  onSuccess?: () => void;
}

/**
 * Generates themed voiceovers for an attraction theme. Routes through the
 * on-demand AI-job pipeline so the user can hand off to background and
 * receive a global notification on completion.
 */
export function GenerateVoiceoversDialog({
  attractionId,
  themeId,
  open,
  onClose,
  onSuccess,
}: GenerateVoiceoversDialogProps) {
  const { voices, isLoading: voicesLoading } = useVoiceOptions();
  const [voiceoverType, setVoiceoverType] = useState<
    'both' | 'description' | 'long_description'
  >('both');
  const [voice, setVoice] = useState<string>('alloy');

  useEffect(() => {
    if (open) setVoiceoverType('both');
  }, [open]);

  // Once voices load, snap the selection to one the backend supports.
  useEffect(() => {
    if (voices.length === 0) return;
    if (!voices.some((v) => v.value === voice)) {
      setVoice(voices[0].value);
    }
  }, [voices, voice]);

  return (
    <AiGenerateDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
          onSuccess?.();
        }
      }}
      title="Generate Themed Voiceovers"
      submitLabel="Generate Voiceovers"
      description="Generate AI-powered voiceovers from the themed descriptions using AI text-to-speech."
      endpoint={`/attractions/${attractionId}/themes/${themeId}/generate-voiceovers`}
      invalidateKeys={[['AttractionTheme', attractionId, themeId]]}
      successMessage="Themed voiceovers generated successfully"
      errorFallback="Failed to generate themed voiceovers"
      buildPayload={() => ({ voiceoverType, voice })}
    >
      <div>
        <label htmlFor="voiceoverType" className="block text-sm font-medium mb-2">
          Voiceover Type <span className="text-destructive">*</span>
        </label>
        <select
          id="voiceoverType"
          required
          className="w-full px-3 py-2 border rounded-md"
          value={voiceoverType}
          onChange={(e) =>
            setVoiceoverType(
              e.target.value as 'both' | 'description' | 'long_description',
            )
          }
        >
          <option value="both">Both (Short + Long)</option>
          <option value="description">Short Description Only</option>
          <option value="long_description">Long Description Only</option>
        </select>
        <p className="text-xs text-muted-foreground mt-1">
          Choose which voiceovers to generate
        </p>
      </div>

      <div>
        <label htmlFor="voice" className="block text-sm font-medium mb-2">
          AI Voice <span className="text-destructive">*</span>
        </label>
        <select
          id="voice"
          required
          className="w-full px-3 py-2 border rounded-md"
          value={voice}
          onChange={(e) => setVoice(e.target.value)}
          disabled={voicesLoading}
        >
          {voices.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </AiGenerateDialog>
  );
}
